import { Link } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { AppShell } from '@/components/layout';
import { RichText } from '@/components/trust/RichText';
import { CrisisNote } from '@/components/trust/CrisisNote';
import { PRIVACY } from '@/content/legal/privacy';
import { TERMS } from '@/content/legal/terms';
import { REFUNDS } from '@/content/legal/refunds';
import { PRIVACY_HI } from '@/content/legal/privacy.hi';
import { TERMS_HI } from '@/content/legal/terms.hi';
import { REFUNDS_HI } from '@/content/legal/refunds.hi';
import type { LegalDoc } from '@/content/legal/types';

export type LegalPageName = 'privacy' | 'terms' | 'refunds';

const DOCS: Record<LegalPageName, LegalDoc> = { privacy: PRIVACY, terms: TERMS, refunds: REFUNDS };
const DOCS_HI: Record<LegalPageName, LegalDoc> = { privacy: PRIVACY_HI, terms: TERMS_HI, refunds: REFUNDS_HI };
const OTHERS: Array<{ page: LegalPageName; key: string }> = [
  { page: 'privacy', key: 'footer.privacy' },
  { page: 'terms', key: 'footer.terms' },
  { page: 'refunds', key: 'footer.refunds' },
];

/**
 * Privacy policy, terms of use and refund policy: numbered sections with stable anchors (the
 * footer links to `/privacy#grievance-officer`), a contents list that stays in view on desktop,
 * and plain prose — legal text reads best as a document, not as a stack of cards.
 */
export const Legal = ({ page }: { page: LegalPageName }) => {
  const t = useT();
  const { locale } = useLocale();
  const doc = (locale === 'hi' ? DOCS_HI : DOCS)[page];

  return (
    <AppShell>
      <div className="grid gap-10 nav:grid-cols-[240px_minmax(0,1fr)] nav:gap-14">
        <nav aria-label={t('legal.contents')} className="nav:sticky nav:top-24 nav:self-start">
          <p className="eyebrow text-ink-muted">{t('legal.contents')}</p>
          <ol className="mt-3 grid gap-0.5 border-l-2 border-line text-label">
            {doc.sections.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="-ml-0.5 flex min-h-[36px] items-center gap-2 border-l-2 border-transparent py-1 pl-3 text-ink-soft hover:border-lagoon-500 hover:text-lagoon-700">
                  <span className="w-5 shrink-0 tabular-nums text-ink-muted">{i + 1}</span>
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
          <ul className="mt-6 hidden gap-1 text-caption nav:grid">
            {OTHERS.filter((o) => o.page !== page).map((o) => (
              <li key={o.page}>
                <Link to={`/${o.page}`} className="font-bold text-lagoon-600 hover:underline">
                  {t(o.key)} →
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <article className="min-w-0 max-w-prose">
          <p className="eyebrow text-lagoon-600">Nurture by ZenQuip</p>
          <h1 className="mt-2 text-display">{doc.title}</h1>
          <p className="mt-2 text-caption text-ink-muted">{t('legal.updated', { date: doc.updated })}</p>
          <p className="mt-6 border-l-4 border-sage-300 pl-5 text-[18px] leading-relaxed text-ink-soft">{doc.summary}</p>

          {doc.sections.map((s, i) => (
            <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} className="scroll-mt-24 border-t border-line pt-8 mt-10 first-of-type:mt-10">
              <h2 id={`${s.id}-h`} className="flex items-baseline gap-3 text-h2">
                <span className="font-display text-[18px] font-extrabold tabular-nums text-sage-500">{String(i + 1).padStart(2, '0')}</span>
                <a href={`#${s.id}`} className="hover:text-lagoon-700">
                  {s.heading}
                </a>
              </h2>
              <div className="mt-4 grid gap-4 text-body leading-relaxed text-ink">
                {s.blocks.map((block, j) =>
                  typeof block === 'string' ? (
                    <p key={j}>
                      <RichText text={block} />
                    </p>
                  ) : (
                    <ul key={j} className="grid gap-2 pl-5 [list-style:disc] marker:text-sage-500">
                      {block.map((item, k) => (
                        <li key={k} className="pl-1">
                          <RichText text={item} />
                        </li>
                      ))}
                    </ul>
                  ),
                )}
              </div>
            </section>
          ))}

          <div className="mt-12 rounded-lg bg-mist p-5">
            <CrisisNote />
          </div>
        </article>
      </div>
    </AppShell>
  );
};

export default Legal;
